import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import reports from "./ReportConfig";
import ReportCard from "./ReportCard";

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const WEBSITE_NAME = process.env.REACT_APP_WEBSITE_NAME || 'myBOQ';
const SHOW_FREE_CREDIT = process.env.REACT_APP_SHOW_FREE_CREDIT === 'true';
const FREE_CREDIT_COUNT = Number(process.env.REACT_APP_FREE_CREDIT_COUNT || 1);

const partners = [
  {
    name: "CEIIT",
    image: "/CEIIT_LOGO.png",
    description: "Enhance your skills in civil engineering estimations with expert-led training and workshops.",
    link: "https://civilengineeringiit.in/"
  }
];

// ---- CLARITY helper ----
const track = (name, props = {}) => {
  if (typeof window !== 'undefined' && window.clarity) {
    window.clarity('event', name, props);
  }
};

const HeroSection = () => {
  // modal state
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [activeRole, setActiveRole] = useState(null);

  const roles = useMemo(() => ([
    {
      key: 'engineers',
      icon: '/engineer.svg',
      label: 'Engineers',
      title: 'For Engineers',
      bullets: [
        'Create SSR-based estimates quickly with accurate quantities.',
        'Generate PWD Estimate + Schedule B for approvals and submissions.',
        'Reuse revisions without re-entering the full estimate.',
      ],
      cta: { text: 'Start Estimation', to: '/signup' }
    },
    {
      key: 'contractors',
      icon: '/contractor.svg',
      label: 'Contractors',
      title: 'For Contractors',
      bullets: [
        'Prepare tender-ready BOQ faster with fewer manual checks.',
        'Track revisions and re-generate reports in minutes.',
        'Reduce Excel dependency and avoid calculation mistakes.',
      ],
      cta: { text: 'Get Started', to: '/signup' }
    },
    {
      key: 'architects',
      icon: '/architect.svg',
      label: 'Architects',
      title: 'For Architects',
      bullets: [
        'Convert drawings/measurements into clean BOQ structure.',
        'Share professional reports with clients and consultants.',
        'Maintain scope changes easily using revisions.',
      ],
      cta: { text: 'Explore Features', to: '/features' }
    },
    {
      key: 'municipal',
      icon: '/municipality.svg',
      label: 'Municipal Staff',
      title: 'For Municipal Staff',
      bullets: [
        'Review SSR items and abstracts faster during approvals.',
        'Standardized report formats reduce verification time.',
        'Keep estimate records organized for audits and future reference.',
      ],
      cta: { text: 'See Reports', to: '/features' }
    },
    {
      key: 'consultants',
      icon: '/consultant.svg',
      label: 'Consultants',
      title: 'For Consultants',
      bullets: [
        'Prepare rate analysis and abstracts with consistent outputs.',
        'Deliver export-ready reports for clients without formatting work.',
        'Manage multiple projects with reusable templates and revisions.',
      ],
      // if /pricing route is disabled, keep /features
      cta: { text: 'Explore Features', to: '/features' }
    },
  ]), []);

  const openRoleModal = (role) => {
    setActiveRole(role);
    setRoleModalOpen(true);
    track('professional_card_open', { placement: 'hero', role: role?.key });
  };

  const closeRoleModal = () => {
    setRoleModalOpen(false);
    track('professional_card_close', { placement: 'hero', role: activeRole?.key });
  };

  // ESC + lock scroll
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeRoleModal();
    };

    if (roleModalOpen) {
      document.addEventListener('keydown', onKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleModalOpen]);

  return (
    <section className="bg-white py-12 px-6 lg:px-20">
      <SEO
        title={`${WEBSITE_NAME} | Civil Estimation Software with SSR Precision`}
        description="Simplify estimation and billing with myBOQ. Get accurate PWD SSR-based tenders, bills, and reports in minutes. Built for engineers, contractors, and consultants."
        keywords="civil estimation software, SSR BOQ, tender generator, PWD estimate tool,BOQ,CIVIL, estimate ,india , maharashtra,pwd,bank estimate,measurement,civil engineering,siliconmount,CEIIT"
        image={`${BASE_URL}/ssrlogo3.png`}
        url={BASE_URL}
      />

      {/* HERO + PARTNER */}
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
        {/* Headline */}
        <div className="space-y-6">
          <h1 className="text-4xl lg:text-3xl font-bold text-gray-900 leading-snug">
            <span className="text-orange-600">myBOQ </span>
            Estimations <br className="hidden lg:block" />
            Simplified with <span className="text-orange-600">PWD SSR Precision</span>
          </h1>

          <p className="text-gray-700 text-lg">
            Prepare accurate estimates, tenders, and bills faster—without missing any compliance details.
          </p>

          {/* Promo Strip (toggleable) */}
          {SHOW_FREE_CREDIT && (
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold">
              <span aria-hidden="true">🎁</span>
              <span>
                {FREE_CREDIT_COUNT} free credit{FREE_CREDIT_COUNT > 1 ? 's' : ''} on first-time signup
              </span>
              <Link
                to="/signup"
                className="ml-2 text-orange-700 underline hover:text-orange-900"
                onClick={() => track('free_credit_badge_click', { placement: 'hero', credits: FREE_CREDIT_COUNT })}
              >
                Claim →
              </Link>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mt-6">
            <a
              href="https://www.youtube.com/watch?v=waJAgQUF6j8&t=292s"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:bg-orange-600 transition duration-300 flex items-center gap-2"
              onClick={() => track('demo_video_click', { placement: 'hero', provider: 'youtube' })}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 22v-20l18 10-18 10z" />
              </svg>
              Watch Demo Video
            </a>

            <a
              href={`${BASE_URL}/myBOQ_Information_Brochure_v2.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold hover:bg-orange-300 transition duration-300"
              onClick={() => track('brochure_download', { placement: 'hero', file: 'myBOQ_Software_Brochure.pdf' })}
            >
              Download Brochure
            </a>

            <Link
              to="/signup"
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold transition duration-300"
              onClick={() => track('apply_cta_click', { placement: 'hero', target: 'signup' })}
            >
              Get Started
            </Link>
          </div>
        </div>

        {/* Partner Card */}
        <div className="pl-4 lg:pl-12 max-w-sm mx-auto lg:mx-0">
          <h3 className="text-xl font-semibold text-gray-700 mb-4 text-center lg:text-left">Our Partners</h3>

          {partners.map((partner, idx) => (
            <div
              key={idx}
              className="bg-gray-50 shadow-md rounded-lg overflow-hidden p-6 text-center hover:shadow-lg transition duration-300"
            >
              <img
                src={partner.image}
                alt={`${partner.name} official logo`}
                className="w-20 h-20 object-contain mx-auto mb-4"
              />
              <p className="text-gray-600 mb-2 text-sm">{partner.description}</p>
              <a
                href={partner.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 font-semibold hover:underline text-sm"
                onClick={() => track('partner_visit', { name: partner.name, placement: 'hero_partner' })}
              >
                Visit {partner.name} →
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* REPORTS AT A GLANCE (old section you said missing) */}
      <div className="mt-10 max-w-7xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Reports at a Glance</h3>
          <Link
            to="/features"
            className="text-sm font-semibold text-orange-600 hover:underline"
            onClick={() => track('nav_click', { item: 'features', placement: 'reports_glance' })}
          >
            View all reports →
          </Link>
        </div>

        <div className="flex flex-wrap gap-4">
          {reports.map((report, idx) => (
            <ReportCard
              key={idx}
              {...report}
              compact
              onTrack={(action, props) =>
                track(action, { placement: 'reports_glance', report: report?.title, ...props })
              }
            />
          ))}
        </div>
      </div>

      {/* BUILT FOR PROFESSIONALS (click -> modal) */}
      <div className="mt-16 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Built For Professionals
          </h2>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Tap a role to see how myBOQ helps in daily estimation work.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-5">
          {roles.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => openRoleModal(item)}
              className="group rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 text-left"
              aria-label={`Open details for ${item.label}`}
            >
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-orange-50 flex items-center justify-center border border-orange-100">
                  <img
                    src={item.icon}
                    alt={`${item.label} icon`}
                    className="h-7 w-7 object-contain"
                    loading="lazy"
                  />
                </div>

                <div className="min-w-0">
                  <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                    {item.label}
                  </h4>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* MODAL */}
      {roleModalOpen && activeRole && (
        <div
          className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Professional workflow details"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeRoleModal();
          }}
        >
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                  <img src={activeRole.icon} alt="" className="h-6 w-6" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">{activeRole.title}</div>
                  <div className="text-xs text-gray-500">{activeRole.label} • myBOQ</div>
                </div>
              </div>

              <button
                type="button"
                onClick={closeRoleModal}
                className="h-10 w-10 rounded-full hover:bg-gray-100 text-gray-700 text-xl flex items-center justify-center"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-5">
              <ul className="space-y-3">
                {activeRole.bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 text-gray-700 text-sm">
                    <span className="mt-1 h-2 w-2 rounded-full bg-orange-500 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to={activeRole.cta.to}
                  className="inline-flex justify-center items-center bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-3 rounded-xl transition"
                  onClick={() => track('professional_modal_cta', { role: activeRole.key, target: activeRole.cta.to })}
                >
                  {activeRole.cta.text}
                </Link>

                <button
                  type="button"
                  onClick={() => {
                    track('professional_modal_demo', { role: activeRole.key });
                    window.open("https://www.youtube.com/watch?v=waJAgQUF6j8&t=292s", "_blank");
                  }}
                  className="inline-flex justify-center items-center bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold px-5 py-3 rounded-xl transition"
                >
                  Watch Demo
                </button>
              </div>

              <p className="text-xs text-gray-400 mt-4">
                Tip: press ESC or tap outside to close.
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
